import { useState, useEffect } from "react";
import Label from "./Label.jsx";
import { theme } from "../../theme.js";

export default function PhoneInput({
  label,
  name,
  phoneValue, 
  setFormData,
  required = false,
  disabled = false,
  countryOptions = [
    {id:33, country: "🇫🇷", code: "+33"},
    {id: 32, country : "🇧🇪", code: "+32"},
    {id: 41, country: "🇨🇭", code: "+41"},
    {id: 1, country: "🇺🇸", code: "+1"},
    {id: 39, country: "🇮🇹", code: "+39"},
    {id: 49, country: "🇩🇪", code: "+49"},
    {id: 34, country: "🇪🇸", code: "+34"},
    {id: 46, country: "🇸🇪", code: "+46"},
  ],
  width = "100%",
}) {
  // Extract country code from phoneValue or use default
  const getCountryCode = (phone) => {
    if (!phone) return "+33";
    // Find matching country code from options
    const parts = phone.split('_');
    return parts.length > 0 ? parts[0] : "+33";
  };

  const getCleanPhoneNumber = (phone) => {
    if (!phone) return "";
    const parts = phone.split('_');
    return parts.length > 1 ? parts[1] : "";
  }

  const [selectedCountryCode, setSelectedCountryCode] = useState(getCountryCode(phoneValue));
  const [cleanPhone, setCleanPhone] = useState(getCleanPhoneNumber(phoneValue));

  useEffect(() => {
    setSelectedCountryCode(getCountryCode(phoneValue));
    setCleanPhone(getCleanPhoneNumber(phoneValue));
  }, [phoneValue]);

  const handleCountryChange = (e) => {
    if (disabled) return;
    const newCountryCode = e.target.value;
    setSelectedCountryCode(newCountryCode);
    setFormData((s) => ({ 
      ...s, 
      [name]: newCountryCode + "_" + cleanPhone,
    }));
  };

  const handlePhoneChange = (e) => {
    if (disabled) return;
    // Remove all non-digits and limit to 11 characters
    const newPhone = e.target.value.replace(/\D/g, '').slice(0, 11);
    setCleanPhone(newPhone);
    setFormData((s) => ({ ...s, [name]: selectedCountryCode + "_" + newPhone }));
  };

  const onPaste = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    setFormData((s) => ({ ...s, [name]: e.clipboardData.getData("text") }));
  };

  return (
    <div style={{ ...styles(disabled).wrapper, width }}>
      <Label label={label} required={required} />
      <div style={styles(disabled).container}>
        <select
          name={name+"CountryCode"}
          value={selectedCountryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          style={styles(disabled).countrySelect}
        >
          <option value="">Pays</option>
          {countryOptions.map(({id, country, code}) => (
            <option key={id} value={code}>
              {country} {code}
            </option>
          ))}
        </select>
        <input
          name={name}
          value={cleanPhone}
          onChange={handlePhoneChange}
          onPaste={onPaste}
          type="tel"
          placeholder="Numéro de téléphone"
          disabled={disabled}
          style={styles(disabled).phoneInput}
        />
      </div>
    </div>
  );
}

const styles = (disabled = false) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing.lg,
    width: "100%",
  },
  container: {
    display: "flex",
    gap: theme.spacing.md,
    marginTop: theme.spacing.xs,
    width: "100%",
  },
  countrySelect: {
    flex: "0 0 140px",
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: theme.fonts.size.sm,
    boxSizing: "border-box",
    fontFamily: "inherit",
    backgroundColor: disabled ? theme.colors.bgLight : theme.colors.white,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    maxWidth: "100px",
  },
  phoneInput: {
    flex: 1,
    padding: `${theme.spacing.md} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: theme.fonts.size.sm,
    boxSizing: "border-box",
    fontFamily: "inherit",
    backgroundColor: disabled ? theme.colors.bgLight : "transparent",
    color: disabled ? theme.colors.textMuted : theme.colors.textInverted,
    cursor: disabled ? "not-allowed" : "text",
    opacity: disabled ? 0.6 : 1,
  },
});
